import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { IconPlayerStopFilled, IconSend2 } from '@rossum/ui/icons/tabler';
import { IconButton, Stack, SvgIcon } from '@rossum/ui/material';
import { AttachmentList } from '../attachments/AttachmentList';
import { AttachmentPlusButton } from '../attachments/AttachmentPlusButton';
import { useAutoFocus } from '../hooks/useAutoFocus';
import { useChatAttachments } from '../hooks/useChatAttachments';
import { DocumentContent, ImageContent } from '../types';
import { ChatTextField } from './ChatTextField';

type ChatBottomPanelProps = {
  onSend: (
    message: string,
    images?: ImageContent[],
    documents?: DocumentContent[]
  ) => void;
  onStop?: () => void;
  isStreaming: boolean;
  isInitializing: boolean;
  focusTrigger?: number;
  mcpReadWrite?: boolean;
  attachments: ReturnType<typeof useChatAttachments>;
};

export const ChatBottomPanel = ({
  onSend,
  onStop,
  isStreaming,
  isInitializing,
  focusTrigger,
  mcpReadWrite,
  attachments,
}: ChatBottomPanelProps) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const [value, setValue] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);

  useAutoFocus(inputRef, focusTrigger ?? 0, !isInitializing);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed || attachments.total > 0) {
      onSend(
        trimmed ||
          intl.formatMessage({
            id: 'features.chat.bottomPanel.fallbackMessage',
          }),
        attachments.imageContents,
        attachments.documentContents
      );
      setValue('');
      setSelectedSuggestion(0);
      attachments.clear();
    }
  }, [value, attachments, onSend, intl]);

  const canSend =
    !isInitializing && (Boolean(value.trim()) || attachments.total > 0);

  const showStop = isStreaming && !value.trim() && attachments.total === 0;

  return (
    <Stack
      spacing={1}
      component="form"
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        handleSend();
      }}
    >
      <AttachmentList
        attachments={attachments.attachmentPreviews}
        onRemove={attachments.removeAttachment}
      />
      <Stack direction="row" spacing={2} alignItems="center">
        <AttachmentPlusButton
          onPick={attachments.addFiles}
          disabled={isInitializing}
          canAddMore={attachments.canAddMore}
          dataCy="chat-add-attachment-button"
        />

        <ChatTextField
          value={value}
          onPaste={attachments.handlePaste}
          setValue={setValue}
          selectedSuggestion={selectedSuggestion}
          setSelectedSuggestion={setSelectedSuggestion}
          placeholder={
            mcpReadWrite
              ? intl.formatMessage({
                  id: 'features.chat.bottomPanel.placeholderWriteMode',
                })
              : intl.formatMessage({
                  id: 'features.chat.bottomPanel.placeholder',
                })
          }
          disabled={isInitializing}
          inputRef={inputRef}
        />

        <IconButton
          data-cy={showStop ? 'chat-stop-button' : 'chat-send-button'}
          type={showStop ? 'button' : 'submit'}
          color="primary"
          variant="contained"
          onClick={showStop ? onStop : undefined}
          disabled={!showStop && !canSend}
        >
          <SvgIcon fontSize="medium">
            {showStop ? <IconPlayerStopFilled /> : <IconSend2 />}
          </SvgIcon>
        </IconButton>
      </Stack>
    </Stack>
  );
};
